import { useEffect } from 'react';
import { useLocation, useMatches } from 'react-router-dom';

import { useTranslation } from '@sshop/i18n';
import type { ProductDetail } from '@sshop/domain';

import { useCategoryDetail } from '@/features/catalog';
import { useNewsArticle } from '@/features/news';
import { useProductDetail } from '@/features/product-details';
import { env } from '@/shared/config/env';
import { useCmsPage } from '@/shared/hooks/useCmsPage';
import { publicAssetUrl, versionedPublicAssetUrl } from '@/shared/lib/publicAssetUrl';
import { routes } from '@/shared/lib/routes';

const BRAND = 'SSHOP';
const PUBLIC_SECTIONS = new Set([
  '', 'categories', 'catalog', 'product', 'about', 'contact', 'guarantee', 'shipping-payment', 'news',
  'privacy-policy', 'terms-conditions',
]);
const CMS_KEYS: Record<string, string> = {
  about: 'about', guarantee: 'guarantee', 'shipping-payment': 'shipping_payment',
  'privacy-policy': 'privacy_policy', 'terms-conditions': 'term_condition',
};

const plainText = (value: string) => {
  const node = document.createElement('div');
  node.innerHTML = value;
  return (node.textContent ?? '').replace(/\s+/g, ' ').trim();
};
const absolute = (url: string) => new URL(url, window.location.origin).href;

function setMeta(attribute: 'name' | 'property', key: string, content: string) {
  let node = Array.from(document.head.querySelectorAll<HTMLMetaElement>(`meta[${attribute}]`))
    .find((candidate) => candidate.getAttribute(attribute) === key);
  if (!node) {
    node = document.createElement('meta');
    node.setAttribute(attribute, key);
    document.head.appendChild(node);
  }
  node.content = content;
}

function removeMeta(attribute: 'name' | 'property', key: string) {
  Array.from(document.head.querySelectorAll<HTMLMetaElement>(`meta[${attribute}]`))
    .find((candidate) => candidate.getAttribute(attribute) === key)
    ?.remove();
}

function setLink(rel: string, href: string, hreflang?: string) {
  let node = Array.from(document.head.querySelectorAll<HTMLLinkElement>('link[rel]'))
    .find((candidate) => candidate.rel === rel && candidate.hreflang === (hreflang ?? ''));
  if (!node) {
    node = document.createElement('link');
    node.rel = rel;
    if (hreflang) node.hreflang = hreflang;
    document.head.appendChild(node);
  }
  node.href = href;
}

function setJsonLd(data: Record<string, unknown> | null) {
  document.getElementById('seo-json-ld')?.remove();
  if (!data) return;
  const script = document.createElement('script');
  script.id = 'seo-json-ld';
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data).replace(/</g, '\\u003c');
  document.head.appendChild(script);
}

function breadcrumbList(items: { name: string; path: string }[]) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem', position: index + 1, name: item.name, item: absolute(item.path),
    })),
  };
}

function productSchema(product: ProductDetail, url: string) {
  return {
    '@type': 'Product', name: product.title, description: plainText(product.description),
    image: product.images.map((image) => absolute(image.url)),
    sku: product.sku,
    ...(product.brand ? { brand: { '@type': 'Brand', name: product.brand } } : {}),
    ...(product.rating !== null && product.reviewCount > 0 ? {
      aggregateRating: {
        '@type': 'AggregateRating', ratingValue: product.rating, reviewCount: product.reviewCount,
      },
    } : {}),
    offers: {
      '@type': 'Offer', url, price: product.price.amount,
      priceCurrency: product.price.currency,
      availability: `https://schema.org/${product.inStock ? 'InStock' : 'OutOfStock'}`,
      itemCondition: 'https://schema.org/NewCondition',
    },
  };
}

export function DocumentMetadata() {
  const { pathname, search } = useLocation();
  const matches = useMatches();
  const segments = pathname.split('/').filter(Boolean);
  const locale = segments[0] === 'en' ? 'en' : 'ge';
  const section = segments[1] ?? '';
  const slug = segments[2] ?? '';
  const isNotFound = matches.some((match) => match.id === 'not-found');
  const { t: commonT } = useTranslation();
  const { t: catalogT } = useTranslation('catalog');
  const { t: productT } = useTranslation('product');
  const { t: cartT } = useTranslation('cart');
  const { t: checkoutT } = useTranslation('checkout');
  const { t: authT } = useTranslation('auth');
  const { t: accountT } = useTranslation('account');
  const { t: staticT } = useTranslation('static');

  const { data: category, isError: categoryError, isPending: categoryPending } =
    useCategoryDetail(section === 'catalog' && slug ? slug : undefined);
  const { data: product, isError: productError, isPending: productPending } =
    useProductDetail(section === 'product' ? slug : '');
  const { data: article, isError: articleError, isPending: articlePending } =
    useNewsArticle(section === 'news' ? slug : '');
  const { data: cmsPage, isError: cmsError, isPending: cmsPending } =
    useCmsPage(section === '' ? 'home' : (CMS_KEYS[section] ?? null));

  useEffect(() => {
    const params = new URLSearchParams(search);
    const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
    const filtered = section === 'catalog' && Array.from(params.keys())
      .some((key) => key !== 'page');
    const missing = (section === 'product' && !productPending && (productError || !product)) ||
      (section === 'news' && !!slug && !articlePending && (articleError || !article)) ||
      (section === 'catalog' && !!slug && !categoryPending && (categoryError || !category));
    const emptyCms = !!CMS_KEYS[section] && !cmsPending && (cmsError || !cmsPage?.description);
    const indexable = env.SEO_INDEXABLE === '1' &&
      PUBLIC_SECTIONS.has(section) && !isNotFound && !missing && !filtered && !emptyCms;
    const canonicalPath = pathname.replace(/\/$/, '') || `/${locale}`;
    const canonicalQuery = section === 'catalog' && !filtered && page > 1 ? `?page=${page}` : '';
    const canonical = absolute(canonicalPath + canonicalQuery);
    const titleBase: Record<string, string> = {
      '': cmsPage?.metaTitle || cmsPage?.title || commonT('home.hero.title'),
      categories: commonT('nav.allCategories'),
      catalog: category?.name || catalogT('title'),
      product: product?.metaTitle || product?.title || productT('catalog'),
      cart: cartT('title'), favorites: commonT('wishlist.title'),
      checkout: slug === 'success' ? checkoutT('successTitle') : checkoutT('title'),
      account: accountT('title'), login: authT('signIn'), register: authT('signUp'),
      'forgot-password': authT('resetPasswordTitle'),
      'reset-password': authT('resetPasswordTitle'), auth: authT('redirecting'),
      about: cmsPage?.metaTitle || cmsPage?.title || staticT('about.title'),
      contact: staticT('contact.title'),
      guarantee: cmsPage?.metaTitle || cmsPage?.title || staticT('guarantee.title'),
      'shipping-payment': cmsPage?.metaTitle || cmsPage?.title || staticT('shippingPayment.title'),
      news: article?.title || staticT('news.title'),
      'privacy-policy': cmsPage?.metaTitle || cmsPage?.title || staticT('privacy.title'),
      'terms-conditions': cmsPage?.metaTitle || cmsPage?.title || staticT('terms.title'),
    };
    const heading = isNotFound || missing
      ? commonT('app.notFound')
      : titleBase[section] ?? BRAND;
    const adminHomeTitle = section === '' ? cmsPage?.metaTitle.trim() : '';
    const title = adminHomeTitle ||
      `${heading}${section === 'catalog' && page > 1 ? ` — ${catalogT('page', { page })}` : ''} | ${BRAND}`;
    const descriptions: Record<string, string> = {
      '': cmsPage?.metaDescription || cmsPage?.description || commonT('home.hero.subtitle'),
      categories: commonT('categories.subtitle'),
      catalog: category?.description || commonT('home.categories.subtitle'),
      product: product?.description || '',
      news: article?.shortDescription || (article ? article.text : staticT('news.title')),
      about: cmsPage?.metaDescription || cmsPage?.description || staticT('about.subtitle'),
      contact: staticT('contact.subtitle'),
      guarantee: cmsPage?.metaDescription || cmsPage?.description || staticT('guarantee.subtitle'),
      'shipping-payment': cmsPage?.metaDescription || cmsPage?.description || staticT('shippingPayment.title'),
      'privacy-policy': cmsPage?.metaDescription || cmsPage?.description || '',
      'terms-conditions': cmsPage?.metaDescription || cmsPage?.description || '',
    };
    const description = plainText(descriptions[section] || heading).slice(0, 300);
    const socialTitle = section === ''
      ? (cmsPage?.metaOgTitle.trim() || title)
      : title;
    const socialDescription = section === ''
      ? plainText(cmsPage?.metaOgDescription || description).slice(0, 300)
      : description;
    const contentImage = product?.images[0]?.url || category?.imageUrl || article?.coverImage ||
      (section === '' ? cmsPage?.imageUrl : null);
    const image = contentImage || publicAssetUrl('social-share-v2.jpg');
    const imageUrl = absolute(image);

    document.documentElement.lang = locale === 'ge' ? 'ka' : 'en';
    document.title = title;
    setMeta('name', 'description', description);
    if (cmsPage?.metaKeywords.trim()) {
      setMeta('name', 'keywords', cmsPage.metaKeywords.trim());
    } else {
      removeMeta('name', 'keywords');
    }
    setMeta('name', 'robots', indexable ? 'index, follow' : 'noindex, follow');
    setMeta('property', 'og:site_name', BRAND);
    setMeta('property', 'og:locale', locale === 'ge' ? 'ka_GE' : 'en_US');
    setMeta('property', 'og:type', section === 'news' && article ? 'article' : section === 'product' && product ? 'product' : 'website');
    setMeta('property', 'og:title', socialTitle);
    setMeta('property', 'og:description', socialDescription);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:image', imageUrl);
    setMeta('property', 'og:image:url', imageUrl);
    setMeta('property', 'og:image:secure_url', imageUrl);
    setMeta('property', 'og:image:alt', socialTitle);
    if (contentImage) {
      removeMeta('property', 'og:image:type');
      removeMeta('property', 'og:image:width');
      removeMeta('property', 'og:image:height');
    } else {
      setMeta('property', 'og:image:type', 'image/jpeg');
      setMeta('property', 'og:image:width', '1200');
      setMeta('property', 'og:image:height', '630');
    }
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', socialTitle);
    setMeta('name', 'twitter:description', socialDescription);
    setMeta('name', 'twitter:image', imageUrl);
    setMeta('name', 'twitter:image:alt', socialTitle);

    document.head.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]')
      .forEach((node) => node.remove());
    if (indexable) {
      setLink('canonical', canonical);
      const gePath = canonicalPath.replace(/^\/(ge|en)(?=\/|$)/, '/ge');
      const enPath = canonicalPath.replace(/^\/(ge|en)(?=\/|$)/, '/en');
      setLink('alternate', absolute(gePath + canonicalQuery), 'ka-GE');
      setLink('alternate', absolute(enPath + canonicalQuery), 'en');
      setLink('alternate', absolute(gePath + canonicalQuery), 'x-default');
    }

    let schema: Record<string, unknown> | null = null;
    if (indexable) {
      const graph: Record<string, unknown>[] = [];
      if (!section) {
        graph.push({ '@type': 'WebSite', name: BRAND, url: absolute(routes.home(locale)),
          inLanguage: locale === 'ge' ? 'ka-GE' : 'en' });
        graph.push({ '@type': 'Organization', name: BRAND, url: absolute(routes.home(locale)),
          logo: absolute(versionedPublicAssetUrl('sshop-logo.png')) });
      }
      if (section === 'catalog' && category) {
        graph.push(breadcrumbList([
          { name: commonT('nav.home'), path: routes.home(locale) },
          { name: commonT('nav.allCategories'), path: routes.categories(locale) },
          ...category.ancestors.map((ancestor) => ({ name: ancestor.name, path: routes.catalog(locale, ancestor.slug) })),
          { name: category.name, path: routes.catalog(locale, category.slug) },
        ]));
      }
      if (section === 'product' && product) {
        graph.push(productSchema(product, canonical));
        const first = product.categories[0];
        graph.push(breadcrumbList([
          { name: commonT('nav.home'), path: routes.home(locale) },
          { name: catalogT('title'), path: routes.catalog(locale) },
          ...(first ? [{ name: first.name, path: routes.catalog(locale, first.slug) }] : []),
          { name: product.title, path: routes.product(locale, product.slug) },
        ]));
      }
      if (section === 'news' && article) {
        graph.push({ '@type': 'NewsArticle', headline: article.title,
          description, image: article.coverImage ? [absolute(article.coverImage)] : undefined,
          datePublished: article.publishedAt || undefined, inLanguage: locale === 'ge' ? 'ka-GE' : 'en',
          mainEntityOfPage: canonical, publisher: { '@type': 'Organization', name: BRAND } });
        graph.push(breadcrumbList([
          { name: commonT('nav.home'), path: routes.home(locale) },
          { name: staticT('news.title'), path: routes.news(locale) },
          { name: article.title, path: routes.newsArticle(locale, article.slug) },
        ]));
      }
      if (graph.length) schema = { '@context': 'https://schema.org', '@graph': graph };
    }
    setJsonLd(schema);
    return () => { setJsonLd(null); };
  }, [pathname, search, section, slug, locale, category, categoryError, categoryPending,
    product, productError, productPending, article, articleError, articlePending,
    cmsPage, cmsError, cmsPending, isNotFound,
    commonT, catalogT, productT, cartT, checkoutT, authT, accountT, staticT]);

  return null;
}
